// Écran « 📊 Tableau de bord » (brique C) : chiffre d'affaires par période + estimations
// URSSAF / impôt pour le micro-entrepreneur. Calcul 100 % côté front à partir des factures
// déjà chargées et des paramètres fiscaux mémorisés (réglages). AUCUN taux n'est codé en dur :
// ils sont éditables ci-dessous, pré-remplis aux valeurs 2026 (vente de marchandises BIC).
// Ce sont des ESTIMATIONS d'aide à la gestion, pas un conseil fiscal.

import { useEffect, useMemo, useState } from 'react'
import MenuNavigation from '@/components/MenuNavigation'
import { fetchFactures, type FactureListItem } from '@/lib/factures'
import {
  fetchReglages,
  enregistrerReglages,
  PARAMETRES_FISCAUX_DEFAUT,
  MILLESIME_TAUX,
  type Reglages,
  type ReglagesPayload,
  type ParametresFiscaux,
} from '@/lib/reglages'
import {
  filtrerFactures,
  calculerTableauBord,
  type BaseCA,
  type LigneCA,
} from '@shared/calculs/tableauBord'

interface Props {
  onRetour?: () => void
  // Quand true, le composant est embarqué dans la page d'accueil (« Tableau de bord ») :
  // pas de fond pleine page ni d'en-tête (menu, titre, bouton retour).
  integre?: boolean
}

type PeriodeType = 'annee' | 'trimestre' | 'mois'

const euro = (n: number): string =>
  n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })
const pad2 = (n: number): string => String(n).padStart(2, '0')
const MOIS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
]

// Bornes [debut, fin] (AAAA-MM-JJ, incluses) de la période sélectionnée.
function bornes(type: PeriodeType, annee: number, trimestre: number, mois: number): [string, string] {
  if (type === 'annee') return [`${annee}-01-01`, `${annee}-12-31`]
  if (type === 'mois') {
    const dernier = new Date(annee, mois, 0).getDate()
    return [`${annee}-${pad2(mois)}-01`, `${annee}-${pad2(mois)}-${pad2(dernier)}`]
  }
  const moisDebut = (trimestre - 1) * 3 + 1
  const moisFin = moisDebut + 2
  const dernier = new Date(annee, moisFin, 0).getDate()
  return [`${annee}-${pad2(moisDebut)}-01`, `${annee}-${pad2(moisFin)}-${pad2(dernier)}`]
}

function libellePeriode(type: PeriodeType, annee: number, trimestre: number, mois: number): string {
  if (type === 'annee') return `Année ${annee}`
  if (type === 'mois') return `${MOIS[mois - 1]} ${annee}`
  return `T${trimestre} ${annee}`
}

// Construit le payload réglages COMPLET en ne changeant que les paramètres fiscaux
// (les autres champs sont conservés tels quels -> on n'écrase rien).
function versPayload(r: Reglages | null, params: ParametresFiscaux): ReglagesPayload {
  return {
    raisonSociale: r?.raisonSociale ?? null,
    adresse: r?.adresse ?? null,
    codePostal: r?.codePostal ?? null,
    ville: r?.ville ?? null,
    pays: r?.pays ?? 'France',
    siret: r?.siret ?? null,
    numeroTva: r?.numeroTva ?? null,
    email: r?.email ?? null,
    telephone: r?.telephone ?? null,
    logoUrl: r?.logoUrl ?? null,
    mentionTva: r?.mentionTva ?? null,
    regimeTva: r?.regimeTva ?? 'franchise',
    conditions: r?.conditions ?? null,
    urlAgenda: r?.urlAgenda ?? null,
    urlCanva: r?.urlCanva ?? null,
    parametresFiscaux: params,
  }
}

const champ =
  'w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-bdspInk focus:border-bdspIndigo focus:outline-none'
const label = 'mb-1 block text-xs font-medium text-stone-600'

export default function TableauBord({ onRetour, integre = false }: Props) {
  const maintenant = new Date()
  const [factures, setFactures] = useState<FactureListItem[] | null>(null)
  const [reglagesActuels, setReglagesActuels] = useState<Reglages | null>(null)
  const [params, setParams] = useState<ParametresFiscaux>(PARAMETRES_FISCAUX_DEFAUT)
  const [erreur, setErreur] = useState<string | null>(null)

  const [base, setBase] = useState<BaseCA>('payees')
  const [type, setType] = useState<PeriodeType>('annee')
  const [annee, setAnnee] = useState(maintenant.getFullYear())
  const [trimestre, setTrimestre] = useState(Math.floor(maintenant.getMonth() / 3) + 1)
  const [mois, setMois] = useState(maintenant.getMonth() + 1)

  const [panneauOuvert, setPanneauOuvert] = useState(false)
  const [enCours, setEnCours] = useState(false)
  const [ok, setOk] = useState(false)

  useEffect(() => {
    let actif = true
    Promise.all([fetchFactures(), fetchReglages()])
      .then(([f, r]) => {
        if (!actif) return
        setFactures(f)
        setReglagesActuels(r)
        if (r?.parametresFiscaux) setParams({ ...PARAMETRES_FISCAUX_DEFAUT, ...r.parametresFiscaux })
      })
      .catch(() => actif && setErreur('Impossible de charger les données.'))
    return () => {
      actif = false
    }
  }, [])

  const [debut, fin] = bornes(type, annee, trimestre, mois)

  const resultat = useMemo(() => {
    const lignes: LigneCA[] = (factures ?? []).map((f) => ({
      statut: f.statut,
      dateFacture: f.dateFacture,
      totalHt: f.totalHt,
    }))
    return calculerTableauBord(filtrerFactures(lignes, base, debut, fin), params)
  }, [factures, base, debut, fin, params])

  const majParam = <K extends keyof ParametresFiscaux>(cle: K, valeur: ParametresFiscaux[K]) => {
    setParams((p) => ({ ...p, [cle]: valeur }))
    setOk(false)
  }

  const enregistrerParams = async () => {
    setEnCours(true)
    setErreur(null)
    setOk(false)
    try {
      const maj = await enregistrerReglages(versPayload(reglagesActuels, params))
      setReglagesActuels(maj)
      setOk(true)
    } catch {
      setErreur("Échec de l'enregistrement des paramètres.")
    } finally {
      setEnCours(false)
    }
  }

  const annees = Array.from({ length: 6 }, (_, i) => maintenant.getFullYear() - i)

  return (
    <div className={integre ? '' : 'min-h-screen bg-bdspBg p-4 sm:p-6'}>
      <div className={integre ? '' : 'mx-auto max-w-4xl'}>
        {!integre && (
          <header className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MenuNavigation />
              <h1 className="text-2xl font-bold text-bdspInk">📊 Tableau de bord</h1>
            </div>
            <button
              type="button"
              onClick={onRetour}
              className="rounded-lg border border-stone-300 bg-white px-3 py-1.5 text-sm font-medium text-bdspInk transition hover:bg-stone-50"
            >
              ← Retour
            </button>
          </header>
        )}

        {erreur && (
          <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            {erreur}
          </div>
        )}

        {/* Sélecteurs de période + base */}
        <div className="mb-4 flex flex-wrap items-end gap-3 rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
          <div>
            <label className={label}>Période</label>
            <select className={champ} value={type} onChange={(e) => setType(e.target.value as PeriodeType)}>
              <option value="annee">Année</option>
              <option value="trimestre">Trimestre</option>
              <option value="mois">Mois</option>
            </select>
          </div>
          <div>
            <label className={label}>Année</label>
            <select className={champ} value={annee} onChange={(e) => setAnnee(Number(e.target.value))}>
              {annees.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
          {type === 'trimestre' && (
            <div>
              <label className={label}>Trimestre</label>
              <select className={champ} value={trimestre} onChange={(e) => setTrimestre(Number(e.target.value))}>
                {[1, 2, 3, 4].map((t) => (
                  <option key={t} value={t}>T{t}</option>
                ))}
              </select>
            </div>
          )}
          {type === 'mois' && (
            <div>
              <label className={label}>Mois</label>
              <select className={champ} value={mois} onChange={(e) => setMois(Number(e.target.value))}>
                {MOIS.map((m, i) => (
                  <option key={m} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className={label}>Chiffre d'affaires basé sur</label>
            <select className={champ} value={base} onChange={(e) => setBase(e.target.value as BaseCA)}>
              <option value="payees">Factures payées (encaissé)</option>
              <option value="emises">Toutes les factures émises</option>
            </select>
          </div>
        </div>

        {!factures ? (
          <p className="text-stone-500">Chargement…</p>
        ) : (
          <>
            {/* CA en évidence */}
            <div className="mb-4 rounded-2xl border border-bdspIndigo bg-white p-5 shadow-sm">
              <p className="text-xs uppercase tracking-wide text-stone-500">
                Chiffre d'affaires — {libellePeriode(type, annee, trimestre, mois)}
              </p>
              <p className="mt-1 text-3xl font-bold text-bdspInk">{euro(resultat.ca)}</p>
              <p className="mt-0.5 text-xs text-stone-500">
                {resultat.nbFactures} facture{resultat.nbFactures > 1 ? 's' : ''} ·{' '}
                {base === 'payees' ? 'encaissé (payées)' : 'facturé (émises + payées)'}
              </p>
            </div>

            {/* Estimations */}
            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
                <p className="text-xs text-stone-500">
                  Cotisations sociales URSSAF{params.acreActive ? ' (ACRE appliquée)' : ''}
                </p>
                <p className="mt-1 text-xl font-bold text-bdspInk">{euro(resultat.cotisationsSociales)}</p>
                <p className="mt-0.5 text-xs text-stone-400">
                  {params.tauxCotisation.toLocaleString('fr-FR')} % du CA
                  {params.acreActive ? ` × (1 − ${params.tauxAcre.toLocaleString('fr-FR')} %)` : ''}
                </p>
              </div>
              <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
                <p className="text-xs text-stone-500">Contribution formation pro (CFP)</p>
                <p className="mt-1 text-xl font-bold text-bdspInk">{euro(resultat.cfp)}</p>
                <p className="mt-0.5 text-xs text-stone-400">{params.tauxCfp.toLocaleString('fr-FR')} % du CA</p>
              </div>

              {params.versementLiberatoire ? (
                <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
                  <p className="text-xs text-stone-500">Impôt sur le revenu (versement libératoire)</p>
                  <p className="mt-1 text-xl font-bold text-bdspInk">
                    {euro(resultat.impotVersementLiberatoire ?? 0)}
                  </p>
                  <p className="mt-0.5 text-xs text-stone-400">
                    {params.tauxVersementLiberatoire.toLocaleString('fr-FR')} % du CA, réglé avec l'URSSAF
                  </p>
                </div>
              ) : (
                <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
                  <p className="text-xs text-stone-500">Base imposable (après abattement)</p>
                  <p className="mt-1 text-xl font-bold text-bdspInk">{euro(resultat.baseImposable ?? 0)}</p>
                  <p className="mt-0.5 text-xs text-stone-400">
                    CA − {params.tauxAbattement.toLocaleString('fr-FR')} % · à reporter ; l'impôt dépend de ton foyer
                  </p>
                </div>
              )}

              <div className="rounded-2xl border border-stone-200 bg-bdspIndigoSoft p-4 shadow-sm">
                <p className="text-xs text-stone-500">Total prélèvements (URSSAF{params.versementLiberatoire ? ' + IR' : ''})</p>
                <p className="mt-1 text-xl font-bold text-bdspInk">{euro(resultat.totalPrelevements)}</p>
                <p className="mt-0.5 text-xs text-stone-400">
                  Reste : {euro(resultat.resteApresPrelevements)}
                  {!params.versementLiberatoire ? ' (avant impôt sur le revenu)' : ''}
                </p>
              </div>
            </div>

            {/* Panneau paramètres fiscaux (éditable, mémorisé) */}
            <div className="mb-4 rounded-2xl border border-stone-200 bg-white shadow-sm">
              <button
                type="button"
                onClick={() => setPanneauOuvert((o) => !o)}
                className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-semibold text-bdspInk"
              >
                <span>⚙️ Mes paramètres fiscaux (taux {MILLESIME_TAUX}, modifiables)</span>
                <span aria-hidden="true">{panneauOuvert ? '▲' : '▼'}</span>
              </button>
              {panneauOuvert && (
                <div className="border-t border-stone-100 p-4">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <label className={label}>Cotisations sociales (% du CA)</label>
                      <input
                        type="number" step="0.01" min="0" className={champ}
                        value={params.tauxCotisation}
                        onChange={(e) => majParam('tauxCotisation', Number(e.target.value))}
                      />
                    </div>
                    <div>
                      <label className={label}>CFP — formation pro (% du CA)</label>
                      <input
                        type="number" step="0.01" min="0" className={champ}
                        value={params.tauxCfp}
                        onChange={(e) => majParam('tauxCfp', Number(e.target.value))}
                      />
                    </div>
                    <div>
                      <label className={label}>Abattement forfaitaire IR (%)</label>
                      <input
                        type="number" step="0.01" min="0" className={champ}
                        value={params.tauxAbattement}
                        onChange={(e) => majParam('tauxAbattement', Number(e.target.value))}
                      />
                    </div>
                    <div>
                      <label className={label}>Versement libératoire IR (% du CA)</label>
                      <input
                        type="number" step="0.01" min="0" className={champ}
                        value={params.tauxVersementLiberatoire}
                        onChange={(e) => majParam('tauxVersementLiberatoire', Number(e.target.value))}
                      />
                    </div>
                    <div>
                      <label className={label}>Réduction ACRE (% des cotisations)</label>
                      <input
                        type="number" step="0.01" min="0" className={champ}
                        value={params.tauxAcre}
                        onChange={(e) => majParam('tauxAcre', Number(e.target.value))}
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 text-sm text-bdspInk">
                      <input
                        type="checkbox"
                        checked={params.versementLiberatoire}
                        onChange={(e) => majParam('versementLiberatoire', e.target.checked)}
                      />
                      J'ai opté pour le versement libératoire de l'impôt
                    </label>
                    <label className="flex items-center gap-2 text-sm text-bdspInk">
                      <input
                        type="checkbox"
                        checked={params.acreActive}
                        onChange={(e) => majParam('acreActive', e.target.checked)}
                      />
                      Je bénéficie de l'ACRE (1re année)
                    </label>
                  </div>
                  <div className="mt-3">
                    <label className={label}>Périodicité de déclaration URSSAF (rappels agenda)</label>
                    <select
                      className={champ}
                      value={params.periodiciteUrssaf}
                      onChange={(e) =>
                        majParam('periodiciteUrssaf', e.target.value as 'mensuel' | 'trimestriel')
                      }
                    >
                      <option value="trimestriel">Trimestrielle</option>
                      <option value="mensuel">Mensuelle</option>
                    </select>
                    <p className="mt-1 text-xs text-stone-400">
                      Sert aux rappels du flux agenda (à activer dans les Réglages).
                    </p>
                  </div>
                  <div className="mt-4 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => void enregistrerParams()}
                      disabled={enCours}
                      className="rounded-lg bg-bdspIndigo px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
                    >
                      {enCours ? 'Enregistrement…' : 'Enregistrer mes paramètres'}
                    </button>
                    {ok && <span className="text-sm text-emerald-700">Enregistré ✓</span>}
                  </div>
                </div>
              )}
            </div>

            {/* Avertissement + sources */}
            <p className="text-xs leading-relaxed text-stone-500">
              ⚠️ Estimations d'aide à la gestion, <strong>pas un conseil fiscal</strong>. Le CA est basé
              sur la date de facture ; l'URSSAF retient la date d'encaissement réelle. Vérifie tes montants
              définitifs sur autoentrepreneur.urssaf.fr et impots.gouv.fr. Taux par défaut : millésime{' '}
              {MILLESIME_TAUX} pour une activité de vente de marchandises (BIC), modifiables ci-dessus.
            </p>
          </>
        )}
      </div>
    </div>
  )
}
